import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { vi, enUS } from 'date-fns/locale';
import { Pencil, Trash, Check, X } from 'lucide-react';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Textarea } from '@freelancer-os/ui/src/components/ui/textarea';
import type { ClientNote } from '@freelancer-os/shared-types';
import { useAuthStore } from '../../../stores/authStore';
import {
  useCreateClientNote,
  useUpdateClientNote,
  useDeleteClientNote,
} from '../../../hooks/useClients';
import i18n from 'i18next';

interface NotesTabProps {
  clientId: string;
  notes: ClientNote[];
}

export function NotesTab({ clientId, notes }: NotesTabProps) {
  const { t } = useTranslation('clients');
  const [draft, setDraft] = useState('');
  const userId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.role);

  const { mutate: createNote, isPending: isCreating } = useCreateClientNote();

  const canEditAll = role === 'OWNER' || role === 'ADMIN';

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    createNote(
      { clientId, content: trimmed },
      { onSuccess: () => setDraft('') },
    );
  };

  return (
    <div className="space-y-4">
      {/* Add note */}
      <div className="border border-surface-200 rounded-md p-3 bg-surface-0">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('notes.addPlaceholder')}
          rows={2}
          className="resize-none border-0 shadow-none p-0 focus-visible:ring-0 text-sm"
        />
        <div className="flex justify-end mt-2">
          <Button
            size="sm"
            disabled={isCreating || !draft.trim()}
            onClick={submit}
          >
            {isCreating ? t('notes.saving') : t('notes.save')}
          </Button>
        </div>
      </div>

      {/* Cutoff hint */}
      {notes.length === 50 && (
        <p className="text-xs text-surface-400 text-center">{t('notes.showingRecent')}</p>
      )}

      {/* Feed */}
      {notes.length === 0 ? (
        <p className="text-sm text-surface-500 text-center py-8">{t('notes.empty')}</p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              clientId={clientId}
              canEdit={note.authorId === userId || canEditAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NoteCard({
  note,
  clientId,
  canEdit,
}: {
  note: ClientNote;
  clientId: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation('clients');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(note.content);
  const locale = i18n.language === 'vi' ? vi : enUS;

  const { mutate: updateNote, isPending: isUpdating } = useUpdateClientNote();
  const { mutate: deleteNote, isPending: isDeleting } = useDeleteClientNote();

  const time = formatDistanceToNow(new Date(note.createdAt), { locale, addSuffix: true });
  const authorName = note.author?.fullName ?? '—';

  const saveEdit = () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === note.content) {
      setIsEditing(false);
      setEditValue(note.content);
      return;
    }
    updateNote(
      { clientId, noteId: note.id, content: trimmed },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  const handleDelete = () => {
    if (!confirm(t('notes.deleteConfirm'))) return;
    deleteNote({ clientId, noteId: note.id });
  };

  return (
    <div className="border border-surface-200 rounded-md p-3 bg-surface-0">
      <div className="flex items-start justify-between mb-1">
        <div className="text-xs text-surface-500">
          <span className="font-medium text-surface-700">{authorName}</span> · {time}
        </div>
        {canEdit && !isEditing && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="p-1 text-surface-400 hover:text-surface-700"
              aria-label={t('notes.edit')}
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-1 text-surface-400 hover:text-red-600 disabled:opacity-50"
              aria-label={t('notes.delete')}
            >
              <Trash className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div>
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            rows={3}
            className="text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setEditValue(note.content);
              }}
            >
              <X className="w-3.5 h-3.5 mr-1" /> {t('notes.cancel')}
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={isUpdating || !editValue.trim()}>
              <Check className="w-3.5 h-3.5 mr-1" /> {isUpdating ? t('notes.saving') : t('notes.save')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-surface-700 whitespace-pre-wrap">{note.content}</p>
      )}
    </div>
  );
}
